import { useMarketplace } from 'hooks';
import moment, { Moment } from 'moment-timezone';
import { FunctionComponent, useEffect, useRef, useState } from 'react';
import CalendarBooking from './CalendarBooking';
import DateSelectorComponent from './DateSelectorComponent';
import HoursBooking from './HoursBooking';
import styles from './PeriodSelectorSection.module.scss';
import clsx from 'clsx';

interface PeriodSelectorSectionProps {
    from: Moment;
    to: Moment;
    labelDate?: string;
    labelTimeStart?: string;
    labelTimeEnd?: string;
    hideEndTime?: boolean;
    readOnly?: boolean;
    className?: string;
    onSelectPeriod: (from: Moment, to: Moment) => void;
}

const getDateFormatted = (date: Moment) => {
    const minutes = date.minutes();
    const dateFormatted = date;

    if (minutes > 0 && minutes <= 15) {
        dateFormatted.add(15 - minutes, 'minutes');
    } else if (minutes > 15 && minutes <= 30) {
        dateFormatted.add(30 - minutes, 'minutes');
    } else if (minutes > 30 && minutes <= 45) {
        dateFormatted.add(45 - minutes, 'minutes');
    } else if (minutes > 45 && minutes <= 59) {
        dateFormatted.add(60 - minutes, 'minutes');
    }

    return dateFormatted;
};

export const PeriodSelectorSection: FunctionComponent<PeriodSelectorSectionProps> = props => {
    const marketplace = useMarketplace();

    const [expanded, setExpanded] = useState({
        calendar: false,
        from: false,
        to: false
    });

    const ref = useRef<HTMLDivElement>(null);

    const expandCalendarHandler = () => {
        setExpanded(prev => ({
            ...prev,
            calendar: !prev.calendar,
            from: false,
            to: false
        }));
    };

    const closeCalendarHandler = () => {
        setExpanded(prev => ({ ...prev, calendar: false }));
    };

    const expandFromHandler = () => {
        setExpanded(prev => ({
            ...prev,
            calendar: false,
            from: !prev.from,
            to: false
        }));
    };

    const closeFromHandler = () => {
        setExpanded(prev => ({ ...prev, from: false }));
    };

    const expandToHandler = () => {
        setExpanded(prev => ({
            ...prev,
            calendar: false,
            from: false,
            to: !prev.to
        }));
    };

    const closeToHandler = () => {
        setExpanded(prev => ({ ...prev, to: false }));
    };

    const selectDateHandler = (selectedDate: Date) => {
        closeCalendarHandler();
        const from = moment.tz(
            `${moment(selectedDate).format('YYYY-MM-DD')} ${props.from.format('LT').toUpperCase()}`,
            'YYYY-MM-DD hh:mm A',
            marketplace.schedule.timeZone
        );
        const to = moment.tz(
            `${moment(selectedDate).format('YYYY-MM-DD')} ${props.to.format('LT').toUpperCase()}`,
            'YYYY-MM-DD hh:mm A',
            marketplace.schedule.timeZone
        );
        props.onSelectPeriod(from, to);
    };

    const selectFromHandler = (selectedHour: string) => {
        closeFromHandler();
        const from = moment.tz(
            `${getDateFormatted(props.from).format('YYYY-MM-DD')} ${selectedHour.toUpperCase()}`,
            'YYYY-MM-DD hh:mm A',
            marketplace.schedule.timeZone
        );

        props.onSelectPeriod(from, props.to);
    };

    const selectToHandler = (selectedHour: string) => {
        closeToHandler();
        const to = moment.tz(
            `${getDateFormatted(props.from).format('YYYY-MM-DD')} ${selectedHour.toUpperCase()}`,
            'YYYY-MM-DD hh:mm A',
            marketplace.schedule.timeZone
        );
        props.onSelectPeriod(props.from, to);
    };

    useEffect(() => {
        ref?.current?.scrollIntoView();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ref, props.readOnly]);

    return (
        <div className={clsx(styles.container, props.className)}>
            <div
                className={clsx(styles.sectionCell, {
                    [styles.marginBottom]: props.className
                })}
            >
                {
                    <div
                        ref={ref}
                        className={styles.titleContainer}
                    >
                        <span className={styles.title}>{props.labelDate}</span>
                    </div>
                }
                <div className={styles.sectionCalendar}>
                    <span className={styles.text}>{props.from.format('dddd, DD MMM YYYY')}</span>
                    <DateSelectorComponent
                        id="date"
                        expanded={expanded.calendar}
                        className={styles.contentDate}
                        onExpand={expandCalendarHandler}
                        onClickAway={closeCalendarHandler}
                    >
                        <CalendarBooking
                            date={getDateFormatted(props.from)}
                            onDaySelect={selectDateHandler}
                        />
                    </DateSelectorComponent>
                </div>
            </div>
            <div style={{ display: 'flex' }}>
                <div
                    className={clsx(styles.sectionCell, {
                        [styles.marginBottom]: props.className
                    })}
                >
                    <div className={styles.titleContainer}>
                        <span className={styles.title}>{props.labelTimeStart}</span>
                    </div>
                    <div className={styles.section}>
                        <span className={clsx(styles.textHours, styles.text)}>
                            {props.from.format('LT').toLowerCase()}
                        </span>
                        <DateSelectorComponent
                            id="hour"
                            expanded={expanded.from}
                            className={styles.contentHours}
                            onExpand={expandFromHandler}
                            onClickAway={closeFromHandler}
                        >
                            <HoursBooking
                                dayMoment={getDateFormatted(props.from)}
                                onSelectHour={selectFromHandler}
                            />
                        </DateSelectorComponent>
                    </div>
                </div>
                {!props.hideEndTime && (
                    <div
                        className={clsx(styles.sectionCell, styles.spacing, {
                            [styles.marginBottom]: props.className
                        })}
                    >
                        <div className={styles.titleContainer}>
                            <span className={styles.title}>{props.labelTimeEnd}</span>
                        </div>
                        <div className={styles.section}>
                            <span className={clsx(styles.textHours, styles.text)}>
                                {props.to.format('LT').toLowerCase()}
                            </span>
                            <DateSelectorComponent
                                id="hour"
                                expanded={expanded.to}
                                className={styles.contentHours}
                                onExpand={expandToHandler}
                                onClickAway={closeToHandler}
                            >
                                <HoursBooking
                                    dayMoment={getDateFormatted(props.to)}
                                    onSelectHour={selectToHandler}
                                />
                            </DateSelectorComponent>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default PeriodSelectorSection;
